import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  BOX_TYPE_LABELS,
  FIELD_STATE,
  SIGNER_ROLE_LABELS,
  toPixels,
  toPoints,
  widgetState,
} from "./templateStudioModel";

const Scroller = styled.div`
  flex: 1;
  overflow: auto;
  background: #e5e7eb;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  cursor: ${(p) => (p.$placing ? "crosshair" : "default")};
`;

const PageFrame = styled.div`
  position: relative;
  background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
  flex-shrink: 0;
  user-select: none;
`;

const PageImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
`;

const PageNumber = styled.div`
  position: absolute;
  top: -20px;
  left: 0;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
`;

const Outline = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: ${(p) => (p.$selected ? 3 : 1.5)}px solid ${(p) => p.$color};
  background: ${(p) => (p.$selected ? p.$fillStrong : p.$fill)};
  border-radius: 2px;
  cursor: pointer;
  z-index: ${(p) => (p.$selected ? 3 : 1)};
  &:hover {
    filter: brightness(0.92);
  }
`;

const Tag = styled.span`
  position: absolute;
  left: -2px;
  top: -16px;
  font-size: 10px;
  line-height: 14px;
  padding: 0 5px;
  border-radius: 3px;
  color: #fff;
  background: ${(p) => p.$color};
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

const SignatureBox = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 2px ${(p) => (p.$selected ? "solid" : "dashed")} ${FIELD_STATE.signature.color};
  background: ${(p) => (p.$selected ? "rgba(124, 58, 237, 0.28)" : FIELD_STATE.signature.fill)};
  border-radius: 3px;
  cursor: ${(p) => (p.$editable ? "move" : "pointer")};
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #4c1d95;
  overflow: hidden;
  touch-action: none;
`;

const ResizeHandle = styled.div`
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${FIELD_STATE.signature.color};
  cursor: nwse-resize;
  touch-action: none;
`;

const HintMark = styled.div`
  position: absolute;
  box-sizing: border-box;
  border-bottom: 2px dotted #a78bfa;
  pointer-events: none;
`;

const Placeholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
`;

const strongFill = (color) => `${color}33`;

const StudioCanvas = ({
  pages,
  images,
  scale,
  fields,
  orphans,
  mappings,
  selectedFieldName,
  onSelectField,
  boxes,
  selectedBoxId,
  onSelectBox,
  onChangeBox,
  canEditBoxes,
  placement,
  onPlace,
  showHints,
  hints,
  focus,
}) => {
  const scrollerRef = useRef(null);
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    if (!focus?.key || !scrollerRef.current) return;
    const escape = typeof CSS !== "undefined" && CSS.escape ? CSS.escape : (v) => String(v).replace(/"/g, '\\"');
    const target = scrollerRef.current.querySelector(`[data-focus-key="${escape(focus.key)}"]`);
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [focus]);

  const startDrag = useCallback(
    (event, box, page, mode) => {
      if (!canEditBoxes) return;
      event.preventDefault();
      event.stopPropagation();
      onSelectBox?.(box.id);
      setDrag({
        boxId: box.id,
        page,
        mode,
        startX: event.clientX,
        startY: event.clientY,
        origin: toPixels(box, scale),
        current: toPixels(box, scale),
      });
    },
    [canEditBoxes, onSelectBox, scale]
  );

  useEffect(() => {
    if (!drag) return undefined;
    const move = (event) => {
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      setDrag((d) => {
        if (!d) return d;
        const next =
          d.mode === "resize"
            ? { ...d.origin, width: Math.max(8, d.origin.width + dx), height: Math.max(8, d.origin.height + dy) }
            : { ...d.origin, left: d.origin.left + dx, top: d.origin.top + dy };
        return { ...d, current: next };
      });
    };
    const up = () => {
      setDrag((d) => {
        if (d) onChangeBox?.(d.boxId, toPoints(d.current, d.page, scale));
        return null;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, onChangeBox, scale]);

  const handlePageClick = (event, page) => {
    if (!placement) {
      onSelectField?.(null);
      onSelectBox?.(null);
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    onPlace?.(page, {
      x: (event.clientX - bounds.left) / scale,
      y: (event.clientY - bounds.top) / scale,
    });
  };

  const fieldByRect = new Map();
  (fields || []).forEach((field) => (field.rects || []).forEach((r) => fieldByRect.set(r, field)));

  return (
    <Scroller ref={scrollerRef} $placing={Boolean(placement)} data-testid="studio-canvas">
      {(pages || []).map((page) => {
        const width = page.width * scale;
        const height = page.height * scale;
        const widgets = [
          ...(fields || []).flatMap((f) => (f.rects || []).filter((r) => r.page_number === page.page_number)),
          ...(orphans || []).filter((r) => r.page_number === page.page_number),
        ];
        return (
          <PageFrame
            key={page.page_number}
            style={{ width, height }}
            data-testid={`studio-page-${page.page_number}`}
            onClick={(event) => handlePageClick(event, page)}
          >
            <PageNumber>Page {page.page_number}</PageNumber>
            {images?.[page.page_number] ? (
              <PageImage src={images[page.page_number]} alt={`Page ${page.page_number}`} draggable={false} />
            ) : (
              <Placeholder>Rendering page {page.page_number}…</Placeholder>
            )}

            {showHints &&
              (hints || [])
                .filter((h) => h.page_number === page.page_number)
                .map((h, i) => <HintMark key={`hint-${i}`} style={toPixels(h, scale)} title={h.text} />)}

            {widgets.map((rect, index) => {
              const field = fieldByRect.get(rect);
              const state = widgetState(rect, field, mappings);
              const palette = FIELD_STATE[state];
              const name = field?.field_name || rect.formstack_name || rect.field_name;
              const selected = Boolean(field) && selectedFieldName === field.field_name;
              const px = toPixels(rect, scale);
              return (
                <Outline
                  key={`${name}-${index}`}
                  style={px}
                  $color={palette.color}
                  $fill={palette.fill}
                  $fillStrong={strongFill(palette.color)}
                  $selected={selected}
                  data-testid="studio-field-outline"
                  data-field-name={name}
                  data-state={state}
                  data-focus-key={field ? `field:${field.field_name}` : undefined}
                  title={`${name} — ${palette.label}`}
                  onClick={(event) => {
                    // While placing a spot, let the click fall through to the page so the
                    // spot lands where the user clicked — even on top of a drawn field.
                    if (placement) return;
                    event.stopPropagation();
                    onSelectField?.(field ? field.field_name : null);
                  }}
                >
                  {selected && <Tag $color={palette.color}>{field.field_label || name}</Tag>}
                </Outline>
              );
            })}

            {(boxes || [])
              .filter((b) => b.page_number === page.page_number)
              .map((box) => {
                const selected = selectedBoxId === box.id;
                const px = drag && drag.boxId === box.id ? drag.current : toPixels(box, scale);
                return (
                  <SignatureBox
                    key={box.id}
                    style={px}
                    $selected={selected}
                    $editable={canEditBoxes}
                    data-testid="studio-signature-box"
                    data-box-id={box.id}
                    data-focus-key={`box:${box.id}`}
                    title={`${SIGNER_ROLE_LABELS[box.role] || box.role} · ${BOX_TYPE_LABELS[box.field_type] || box.field_type}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelectBox?.(box.id);
                    }}
                    onPointerDown={(event) => startDrag(event, box, page, "move")}
                  >
                    {SIGNER_ROLE_LABELS[box.role] || box.role} · {BOX_TYPE_LABELS[box.field_type] || box.field_type}
                    {selected && canEditBoxes && (
                      <ResizeHandle
                        data-testid="studio-box-resize"
                        onPointerDown={(event) => startDrag(event, box, page, "resize")}
                      />
                    )}
                  </SignatureBox>
                );
              })}
          </PageFrame>
        );
      })}
    </Scroller>
  );
};

export default StudioCanvas;
